import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, GitBranch, FileText, Gauge, Cpu, ClipboardText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { trackEvent } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwaW90JTIwbWFudWZhY3R1cmluZyUyMGNsZWFufGVufDB8fHx8MTc4NzkwMzM5M3ww&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: Cpu, title: "Describe the product", body: "Category, connectivity, capabilities and target markets drive which frameworks are genuinely in scope." },
  { icon: ClipboardText, title: "Answer the guided assessment", body: "Ten focused areas covering firmware integrity, remote access, SBOM, interfaces and lifecycle." },
  { icon: Gauge, title: "See indicative readiness", body: "Deterministic scores per framework with risk bands, strengths and potential gaps." },
  { icon: FileText, title: "Unlock the gap report", body: "Findings register, evidence guidance, remediation actions and a 30/60/90 day roadmap." },
];

export default function Home() {
  const navigate = useNavigate();
  const startFlow = () => {
    trackEvent("landing_cta_clicked", { location: "hero" });
    navigate("/register");
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <section className="blueprint border-b border-slate-200">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div className="stagger">
            <p className="overline text-navy-800">EU CRA · IEC 62443-4-1 · IEC 62443-4-2 · EU MR · UKCA · UK PSTI</p>
            <h1 className="mt-5 max-w-2xl font-display text-4xl font-extrabold leading-[1.05] text-navy-900 sm:text-5xl lg:text-6xl">
              Know where your connected product actually stands.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
              A focused readiness assessment for product security managers at industrial, IoT and
              embedded manufacturers. Deterministic scoring across six frameworks, EU CRA, IEC
              62443-4-1, IEC 62443-4-2, the EU Machinery Regulation, the UKCA route and UK PSTI,
              with potential gaps named in engineering language.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={startFlow}
                className="bg-navy-800 text-white hover:bg-navy-700"
                data-testid="hero-check-product-button"
              >
                Check my product <ArrowRight size={17} className="ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/cra-readiness")} data-testid="hero-learn-more-button">
                See what the report contains
              </Button>
            </div>
            <p className="mt-6 max-w-lg text-xs leading-relaxed text-slate-500">
              Free while in early access. Indicative scores, the full gap report and PDF export are
              all included, with unlimited reassessment and no card required.
            </p>
          </div>

          <div className="lg:pt-2">
            <img
              src={HERO_IMG}
              alt="Industrial manufacturing equipment"
              className="h-56 w-full border border-slate-200 object-cover lg:h-[300px]"
            />
            <div className="mt-6 border border-slate-200 bg-white p-6">
              <p className="overline text-navy-800">Sample readiness output</p>
              <div className="mt-4 space-y-3">
                {[
                  ["EU CRA", 62, "#0F766E"],
                  ["IEC 62443-4-1", 48, "#B45309"],
                  ["IEC 62443-4-2", 71, "#0369A1"],
                  ["EU MR", 55, "#0F766E"],
                  ["UKCA (GB route)", 66, "#0369A1"],
                  ["UK PSTI", 58, "#0F766E"],
                ].map(([label, val, color]) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs text-slate-600">
                      <span className="font-mono">{label}</span>
                      <span className="font-semibold">{val}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full bg-slate-100">
                      <div className="h-1.5" style={{ width: `${val}%`, background: color }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-slate-500">
                Illustrative values. Indicative readiness only, not a compliance statement.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
        <div className="max-w-2xl">
          <p className="overline text-navy-800">How it works</p>
          <h2 className="mt-4 font-display text-3xl font-bold text-navy-900">
            Four steps from product profile to prioritised gaps
          </h2>
        </div>
        <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white p-7">
              <s.icon size={26} weight="duotone" className="text-navy-800" />
              <p className="mt-5 font-mono text-xs text-slate-400">0{i + 1}</p>
              <h3 className="mt-1 font-display text-base font-bold text-navy-900">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-3 lg:px-8 lg:py-20">
          <div>
            <GitBranch size={26} weight="duotone" className="text-navy-800" />
            <h3 className="mt-5 font-display text-lg font-bold text-navy-900">Cross-framework impact</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              One missing signed-update mechanism does not fail one framework, it reduces evidence
              coverage across CRA, 62443-4-1, 62443-4-2, the Machinery Regulation, UKCA and UK PSTI
              route at once. We show you exactly where that happens.
            </p>
          </div>
          <div>
            <ShieldCheck size={26} weight="duotone" className="text-navy-800" />
            <h3 className="mt-5 font-display text-lg font-bold text-navy-900">Deterministic, not generated</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Scores, severity, findings and roadmap sequencing come from fixed rules and versioned
              mappings. No language model decides your readiness.
            </p>
          </div>
          <div>
            <FileText size={26} weight="duotone" className="text-navy-800" />
            <h3 className="mt-5 font-display text-lg font-bold text-navy-900">Uncertainty stays visible</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              "Don't Know" answers score neutrally, but they are surfaced as validation risk: you
              cannot evidence what the organisation cannot describe.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
        <div className="border border-slate-200 bg-navy-900 p-10 lg:p-14">
          <p className="overline text-blue-300">Start free</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold text-white lg:text-4xl">
            Get indicative scores for your product in under 20 minutes.
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300">
            Create an account, add a product profile and complete the guided assessment. Scores and a
            gap preview are free, unlock the full register when you need it.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={startFlow}
              className="bg-white text-navy-900 hover:bg-slate-100"
              data-testid="footer-cta-button"
            >
              Check my product <ArrowRight size={17} className="ml-2" />
            </Button>
            <Link
              to="/iec-62443"
              className="inline-flex items-center px-5 text-sm text-slate-200 underline decoration-slate-500 underline-offset-4 hover:text-white"
            >
              Read about IEC 62443 scope
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
